import React from 'react';
import { createRouterState } from 'mobx-state-router';

import { routes, UiRouterStore } from './routes';
import { SessionsStore } from './sessions';
import { AuthStore } from './auth';
import { CardsStore } from './cards';
import { DeletedCardsStore } from './deleted-cards';
import { CommentsStore } from './comments';
import { SourcesStore } from './sources';

import { UiThemeStore, getUiThemeStore } from './ui-theme';
import { UiCommentsStore } from './ui-comments';
import { UiCardTitleStore } from './ui-card-title';
import { UiFilenameStore } from './ui-filename';
import { UiSessionNameStore } from './ui-session-name';
import { UiExportQueueStore } from './ui-export-queue';
import { UiExportSourceStore } from './ui-export-source';
import { UiSourceSearchStore } from './ui-source-search';
import { UiLibrarySearchStore } from './ui-library-search';
import { UiProjectSearchStore } from './ui-project-search';
import { UiSourceFieldsStore } from './ui-source-fields';
import { UiCardSearchStore } from './ui-card-search';
import { UiSnackbarStore } from './ui-snackbar';
import { UiKeyboardShortcutsStore } from './ui-keyboard-shortcuts';
import { UiBatchCardsStore } from './ui-batch-cards';
import { UiCardsStore } from './ui-cards';
import { UiActiveSessionStore } from './ui-active-session';
import { UiModulePowerColumnStore } from './ui-module-power-column';
import { UiCardDragDropStore } from './ui-card-dragdrop';
import { UiModuleDragDropStore } from './ui-module-dragdrop';
import { UiImageSourceUploads } from './ui-image-source-upload';
import { UiImageModal } from './ui-image-modal';
import { UiSourceResizeStore } from './ui-source-resize';
import { UiPermissionStore } from './ui-permissions';
import { UserSettingsStore } from './user-settings';
import { UiModuleSourceEditorStore } from './ui-module-source-editor';
import { UiStickyHeadersStore } from './ui-sticky-headers';
import { UiDownloadFoldersStore } from './ui-download-folders';
import { UiBatchCopyStore } from './ui-batch-copy';
import { UiSourceTabDragDropStore } from './ui-source-tab-dragdrop';
import { UiQueueScrollStore } from './ui-list-scroll';
import { SessionCardActionsStore } from './session-card-actions';
import { UiCardView } from './ui-card-view';
import { UiQueueSelectorStore } from './ui-queue-selector-store';
import { UiModuleConnectionDisplayStore } from './ui-module-connection-display';
import { UiTimeSavedStore } from './ui-time-saved';
import { UiProjectNotesStore } from '../stores/UiProjectNotesStore';
import { UiProjectMilestonesStore } from './ui-project-milestones';
import { UiLibrarySlideoutStore } from './ui-library-slideout';
import { UiAccessRequestStore } from './ui-access-request';
import { UiDuplicationRequestStore } from './ui-duplication-request';
import { UiSourceSortStore } from './ui-source-sort';
import { UiSourceRowSelectorStore } from './ui-source-row-selector';
import { SessionIntegrityUtil } from './session-integrity';
import { UiOrganizationMemberStore } from './ui-organization-member';
import { UiOrganizationStatsStore } from './ui-organization-stats';
import { UiCQAdminStore } from './ui-cq-admin';
import { UiOrganizationSessionStore } from './ui-organization-session-management';
import { UiTeamsStore } from './ui-teams';
import { UiSessionSharingStore } from './ui-session-sharing';
import { UiCardDateStore } from './ui-card-date';
import { UiCardPriorityStore } from './ui-card-priority';
import { UiCardIssueStore } from './ui-card-issue';
import { UiInfoPanelStore } from './ui-info-panel';
import { UiAssetsStore } from './ui-assets';
import { AssetCollectionsStore } from './asset-collections';
import { UiSourceFilterStore } from './ui-source-filter';
import { UiSavedViewsStore } from './ui-saved-views';
import { UiBreakoutStore } from './ui-breakout';

const notFound = createRouterState('notFound');

export class RootStore {
  routerStore: UiRouterStore;
  sessionsStore: SessionsStore;
  cardsStore: CardsStore;
  deletedCardsStore: DeletedCardsStore;
  commentsStore: CommentsStore;
  sourcesStore: SourcesStore;
  authStore: AuthStore;
  userSettingsStore: UserSettingsStore;
  uiThemeStore: UiThemeStore;
  uiCardDragDrop: UiCardDragDropStore;
  uiSourceTabDragDrop: UiSourceTabDragDropStore;
  uiCommentsStore: UiCommentsStore;
  uiCardTitleStore: UiCardTitleStore;
  uiFilenameStore: UiFilenameStore;
  uiSessionNameStore: UiSessionNameStore;
  uiExportQueueStore: UiExportQueueStore;
  uiExportSourceStore: UiExportSourceStore;
  uiSourceSearchStore: UiSourceSearchStore;
  uiLibrarySearchStore: UiLibrarySearchStore;
  uiProjectSearchStore: UiProjectSearchStore;
  uiSourceFieldsStore: UiSourceFieldsStore;
  uiSourceResize: UiSourceResizeStore;
  uiCardSearchStore: UiCardSearchStore;
  uiSnackbarStore: UiSnackbarStore;
  uiKeyboardShortcutsStore: UiKeyboardShortcutsStore;
  uiBatchCardsStore: UiBatchCardsStore;
  uiCardsStore: UiCardsStore;
  uiActiveSessionStore: UiActiveSessionStore;
  uiModulePowerColumnStore: UiModulePowerColumnStore;
  uiModuleDragDrop: UiModuleDragDropStore;
  uiImageSourceUpload: UiImageSourceUploads;
  uiImageModal: UiImageModal;
  uiPermissions: UiPermissionStore;
  uiModuleSourceEditor: UiModuleSourceEditorStore;
  uiStickyHeaders: UiStickyHeadersStore;
  uiDownloadFolders: UiDownloadFoldersStore;
  uiBatchCopyStore: UiBatchCopyStore;
  uiQueueScroll: UiQueueScrollStore;
  uiCardView: UiCardView;
  sessionCardActions: SessionCardActionsStore;
  uiQueueSelector: UiQueueSelectorStore;
  uiModuleConnectionDisplayStore: UiModuleConnectionDisplayStore;
  uiTimeSavedStore: UiTimeSavedStore;
  uiProjectNotesStore: UiProjectNotesStore;
  uiProjectMilestonesStore: UiProjectMilestonesStore;
  uiLibrarySlideoutStore: UiLibrarySlideoutStore;
  uiAccessRequestStore: UiAccessRequestStore;
  uiDuplicationRequestStore: UiDuplicationRequestStore;
  uiSourceSortStore: UiSourceSortStore;
  uiOrganizationMemberStore: UiOrganizationMemberStore;
  uiOrganizationStatsStore: UiOrganizationStatsStore;
  uiCQAdminStore: UiCQAdminStore;
  uiOrganizationSessionStore: UiOrganizationSessionStore;
  uiTeamsStore: UiTeamsStore;
  uiSessionSharingStore: UiSessionSharingStore;
  uiCardDateStore: UiCardDateStore;
  uiCardPriorityStore: UiCardPriorityStore;
  uiCardIssueStore: UiCardIssueStore;
  uiInfoPanelStore: UiInfoPanelStore;
  uiSourceRowSelector: UiSourceRowSelectorStore;
  uiAssetsStore: UiAssetsStore;
  assetCollectionsStore: AssetCollectionsStore;
  uiSourceFilterStore: UiSourceFilterStore;
  uiSavedViewsStore: UiSavedViewsStore;
  uiBreakoutStore: UiBreakoutStore;
  sessionIntegrityUtil: SessionIntegrityUtil;

  constructor() {
    // Initialize core stores first
    this.routerStore = new UiRouterStore(this, routes, notFound);
    this.authStore = new AuthStore(this);
    this.sessionsStore = new SessionsStore(this);
    this.cardsStore = new CardsStore(this);
    this.deletedCardsStore = new DeletedCardsStore(this);
    this.commentsStore = new CommentsStore(this);
    this.sourcesStore = new SourcesStore(this);
    this.userSettingsStore = new UserSettingsStore(this);

    // Initialize UI stores after core stores
    this.uiThemeStore = getUiThemeStore();
    this.uiCardDragDrop = new UiCardDragDropStore(this);
    this.uiCommentsStore = new UiCommentsStore();
    this.uiCardTitleStore = new UiCardTitleStore(this);
    this.uiFilenameStore = new UiFilenameStore(this);
    this.uiSessionNameStore = new UiSessionNameStore(this);
    this.uiExportQueueStore = new UiExportQueueStore(this);
    this.uiExportSourceStore = new UiExportSourceStore(this);
    this.uiSourceSearchStore = new UiSourceSearchStore();
    this.uiLibrarySearchStore = new UiLibrarySearchStore();
    this.uiProjectSearchStore = new UiProjectSearchStore(this);
    this.uiSourceFieldsStore = new UiSourceFieldsStore(this);
    this.uiSourceResize = new UiSourceResizeStore();
    this.uiCardSearchStore = new UiCardSearchStore(this);
    this.uiSnackbarStore = new UiSnackbarStore(this);
    this.uiKeyboardShortcutsStore = new UiKeyboardShortcutsStore(this);
    this.uiBatchCardsStore = new UiBatchCardsStore(this);
    this.uiCardsStore = new UiCardsStore();
    this.uiActiveSessionStore = new UiActiveSessionStore(this);
    this.uiModulePowerColumnStore = new UiModulePowerColumnStore(this);
    this.uiModuleDragDrop = new UiModuleDragDropStore(this);
    this.uiImageSourceUpload = new UiImageSourceUploads(this);
    this.uiImageModal = new UiImageModal(this);
    this.uiPermissions = new UiPermissionStore(this);
    this.uiModuleSourceEditor = new UiModuleSourceEditorStore(this);
    this.uiStickyHeaders = new UiStickyHeadersStore();
    this.uiDownloadFolders = new UiDownloadFoldersStore(this);
    this.uiBatchCopyStore = new UiBatchCopyStore(this);
    this.uiSourceTabDragDrop = new UiSourceTabDragDropStore(this);
    this.uiQueueScroll = new UiQueueScrollStore(this);
    this.uiCardView = new UiCardView(this);
    this.sessionCardActions = new SessionCardActionsStore(this);
    this.uiQueueSelector = new UiQueueSelectorStore(this);
    this.uiModuleConnectionDisplayStore = new UiModuleConnectionDisplayStore(this);
    this.uiTimeSavedStore = new UiTimeSavedStore(this);
    this.uiProjectNotesStore = new UiProjectNotesStore(this);
    this.uiProjectMilestonesStore = new UiProjectMilestonesStore(this);
    this.uiLibrarySlideoutStore = new UiLibrarySlideoutStore();
    this.uiAccessRequestStore = new UiAccessRequestStore(this);
    this.uiDuplicationRequestStore = new UiDuplicationRequestStore(this);
    this.uiSourceSortStore = new UiSourceSortStore();
    this.uiOrganizationMemberStore = new UiOrganizationMemberStore(this);
    this.uiOrganizationStatsStore = new UiOrganizationStatsStore(this);
    this.uiCQAdminStore = new UiCQAdminStore(this);
    this.uiOrganizationSessionStore = new UiOrganizationSessionStore(this);
    this.uiTeamsStore = new UiTeamsStore(this);
    this.uiSessionSharingStore = new UiSessionSharingStore(this);
    this.uiCardDateStore = new UiCardDateStore(this);
    this.uiCardPriorityStore = new UiCardPriorityStore(this);
    this.uiCardIssueStore = new UiCardIssueStore(this);
    this.uiInfoPanelStore = new UiInfoPanelStore();
    this.uiSourceRowSelector = new UiSourceRowSelectorStore(this);
    this.uiAssetsStore = new UiAssetsStore(this);
    this.assetCollectionsStore = new AssetCollectionsStore(this);
    this.uiSourceFilterStore = new UiSourceFilterStore();
    this.uiSavedViewsStore = new UiSavedViewsStore(this);
    this.uiBreakoutStore = new UiBreakoutStore(this);

    // Initialize utility after all stores are created
    this.sessionIntegrityUtil = new SessionIntegrityUtil(this);
  }

  init() {
    // Initialize core stores first
    this.authStore.init();
    this.sessionsStore.init();
    this.cardsStore.init();
    this.deletedCardsStore.init();
    this.commentsStore.init();
    this.sourcesStore.init();
    this.assetCollectionsStore.init();
    this.userSettingsStore.init();

    // Initialize UI stores after core stores
    this.uiThemeStore.init();
    this.uiSourceFieldsStore.init();
    this.uiSavedViewsStore.init();
    this.uiKeyboardShortcutsStore.init();
    this.uiSnackbarStore.init();
    this.uiCardView.init();
    this.uiDuplicationRequestStore.init();
  }

  dispose() {
    // Dispose stores that have reactions or listeners that need cleanup
    this.uiProjectNotesStore.dispose();
    this.uiDuplicationRequestStore.dispose();
    this.uiAssetsStore.dispose();
    this.assetCollectionsStore.dispose();
  }
}

export const storeContext = React.createContext<RootStore | undefined>(undefined);

/**
 * Resolves the app RootStore from React context, with a dev-only fallback to `window.rootStore`
 * (set in `index.tsx`) when Vite HMR swaps module instances and the default-context trap would
 * otherwise yield an empty object and crash nested destructuring like `authStore: { user }`.
 */
export function useStore(): RootStore {
  const fromContext = React.useContext(storeContext);
  if (fromContext?.authStore) {
    return fromContext;
  }

  const fromWindow =
    typeof window !== 'undefined'
      ? (window as unknown as { rootStore?: RootStore }).rootStore
      : undefined;

  if (fromWindow?.authStore) {
    return fromWindow;
  }

  throw new Error(
    'Creative Queue RootStore is not available (missing storeContext.Provider). Refresh the page.',
  );
}
